import type { LegalDocumentWords } from "./legal-words.ts";

// The Terms of Service in English, the version that controls (ADR-024, D701). Every numbered rule
// here has a reason in ADR-024, ADR-032 or the decisions log; change the Spanish text in the same
// commit. A change people must accept again also moves `TERMS_VERSION` (onboarding's contract).

export const TERMS_EN: LegalDocumentWords = {
  title: "Terms of Service",
  summary:
    "These terms are the contract between you and {company} when you use {product}. They cover what you buy, how refunds work, what you may do with the data, and which law applies.",
  seoDescription: "The terms for using {product}: what you buy, refunds, data use and the law that applies.",
  sections: [
    {
      id: "scope",
      heading: "1. Who we are and who these terms are for",
      body: [
        '{product} is run by {company}, {address} ("we", "us"). The full company details are in the Legal Notice.',
        "{product} is sold to businesses and freelancers who use it for their trade or profession. By creating an account you confirm that you act in that capacity and not as a consumer.",
        "These terms apply to the website, the app and any paid plan, credit pack or add-on. Terms of your own do not apply, even if we do not object to them.",
      ],
    },
    {
      id: "service",
      heading: "2. What {product} does",
      body: [
        "{product} finds prospects among the public followers, likers and commenters of social accounts you choose, enriches them with publicly available contact details, and lets you organise them and email them from your own mailboxes.",
        "We read public profile data only. We do not sign in to anyone's social account to read it, and we do not read private profiles or messages. We obtain the data through a specialised data provider.",
        "{product} is not affiliated with, endorsed by or connected to Instagram or Meta Platforms, Inc. Instagram is a trademark of its owner.",
        "A social platform may change what it shows at any time. We therefore cannot promise that a given profile, field or number of results will be available.",
      ],
    },
    {
      id: "account",
      heading: "3. Your account",
      body: [
        "You must be at least 18 years old and give true details. Keep your password and any API key secret; you are responsible for what happens under your account.",
        "An organization's owner decides who joins it. Members act on the organization's behalf, and the organization is bound by what they do within their role.",
      ],
    },
    {
      id: "plans",
      heading: "4. Plans, credits and payment",
      body: [
        "The plans, their limits and their prices are on the pricing page at the time you buy. Statutory VAT is added where it applies.",
        "A paid plan renews for the same period, monthly or yearly, until you cancel it. Each period starts with the plan's credits; unused plan credits do not carry over.",
        "Reading one Instagram profile costs one credit, whether or not the profile shows an email address. Other sources, when offered, show their own price before you start.",
        "Credit packs, AI credits and add-on hours you buy separately carry over until used.",
        "You pay in advance, by card through our payment provider or in bitcoin where offered. If a payment fails, we tell you and may pause the paid features until it succeeds.",
      ],
    },
    {
      id: "refunds",
      heading: "5. Refunds",
      body: [
        "We refund a plan's first payment if you ask within 14 days of it. This is our own promise; the statutory withdrawal right for consumers does not apply to business customers.",
        "The promise does not apply:",
        {
          list: [
            "once you have used 25% of that plan's credits, or 2,500 credits, whichever comes first;",
            "to a purchase made with a coupon or a promotional price;",
            "to a renewal: cancelling stops the next renewal instead.",
          ],
        },
        "A credit pack is refunded within 14 days of purchase while none of its credits are used.",
        "Write to {contact} from the account's email address. We refund within 14 days, to the method you paid with. A bitcoin payment is refunded at its value in euros on the day you paid it, to an address you give us.",
      ],
    },
    {
      id: "disputes",
      heading: "6. Payment disputes",
      body: [
        "Please write to us before you dispute a charge with your bank. If you open a dispute without asking us first and the charge was correct, you pay the fees our payment provider charged us for the dispute.",
        "While a dispute is open we may pause the account. When it ends in our favour, the amount remains due.",
      ],
    },
    {
      id: "use",
      heading: "7. How you may use the data",
      body: [
        "You decide whom {product} reads, which contacts you export and what you do with them. You are the controller of that contact data and responsible for using it lawfully.",
        "You warrant that you have a lawful basis for every use you make of it. That covers emailing people, keeping them in a CRM, and uploading them to an ad platform such as Meta custom audiences. Where you rely on legitimate interest, you have weighed it against the interests of the people concerned. In particular you must:",
        {
          list: [
            "have a lawful basis under the GDPR, and under any other law that applies, for each person you keep, contact or upload;",
            "give each person in the EU the information Art. 14 GDPR asks for, including where their data came from. Do it within one month of obtaining it, and at the latest when you first contact them;",
            "have the prior consent the law asks for before you send advertising by email. In Germany, § 7 UWG asks for it from businesses too;",
            "honour every objection, opt-out and removal request at once: stop contacting the person, and remove them from your lists, your CRM and every audience you uploaded. Keep the unsubscribe link {product} adds to each email;",
            "follow the terms of the mailboxes, ad platforms and other services you connect or upload to, including any promise of a lawful basis they ask of you.",
          ],
        },
        "You may upload contacts you exported to an ad platform for your own advertising, if you meet the duties above and that platform's terms. Never use special categories of data, or anything that reveals them, to choose or describe an audience.",
        "You must not use {product} to:",
        {
          list: [
            "send spam, or harass, stalk, threaten or deceive anyone;",
            "target children, or collect health, political, religious or other special categories of data;",
            "sell, rent, publish or share lists, or the contacts in them, with third parties. Your own upload to an ad platform, as allowed above, is not sharing;",
            "discriminate, or build profiles to deny anyone housing, work, credit or insurance;",
            "break the law, infringe anyone's rights, or send malicious content;",
            "copy, scrape, reverse-engineer or overload {product}, or get around its limits or security.",
          ],
        },
      ],
    },
    {
      id: "processing",
      heading: "8. Processing data on your behalf",
      body: [
        "For the lists you build, the contacts you export and the emails you send, we process personal data on your behalf as your processor (Art. 28 GDPR). You are the controller for that data, and for every copy you export or upload elsewhere; this section is our processing agreement.",
        {
          list: [
            "Subject and duration: the prospects, lists, campaigns and replies in your workspace, for as long as your account exists.",
            "Kinds of data: public profile data such as name, username, bio, follower counts, location and website. Also a public email address or phone number, the emails you send and the replies you receive.",
            "People concerned: the people whose public profiles you have read, and the people you email.",
            "We act only on your documented instructions, which are these terms and the settings you choose, and tell you if we think an instruction breaks the law.",
            "Everyone who handles the data is bound to confidentiality.",
            "We protect it with the technical and organisational measures described in the Privacy Policy.",
            "We use the subprocessors listed in the Privacy Policy. We announce a new one 30 days ahead; if you object on reasonable grounds, you may end the contract before it starts.",
            "We help you answer requests from the people concerned and meet your duties on security, breaches and impact assessments.",
            "We tell you without undue delay of a personal data breach that affects your data.",
            "When your account is deleted, we delete the data, unless the law asks us to keep it.",
            "We give you the information you need to show that these duties are met, and allow audits on reasonable notice.",
          ],
        },
      ],
    },
    {
      id: "ai",
      heading: "9. AI features and agents",
      body: [
        "AI features suggest text and actions. They can be wrong. Check what they write before you send it; you are responsible for what goes out under your name.",
        "If you connect an account to an agent, such as a mailbox or a login stored in your vault, the agent acts on your instructions with that account. Connect only accounts you may use this way.",
      ],
    },
    {
      id: "ownership",
      heading: "10. Ownership",
      body: [
        "{product}, its software and its design belong to us. You get the right to use them while your account exists, within these terms.",
        "Your lists, templates and content stay yours. You allow us to process them only to provide {product} to you.",
      ],
    },
    {
      id: "term",
      heading: "11. Ending the contract",
      body: [
        "You may cancel a paid plan at any time in the app. It ends at the end of the period you paid for, and the account moves to the free plan.",
        "You may delete your account at any time. Export your lists first: deletion removes them after a short grace period.",
        "We may end the contract with 30 days' notice by email. We then refund the part you paid for but will not receive.",
        "We may suspend or end an account at once for good cause. Examples are a serious or repeated breach of section 7, fraud, or a risk to {product} or to other customers. No refund is due in that case.",
      ],
    },
    {
      id: "availability",
      heading: "12. Availability and changes to the service",
      body: [
        "We work to keep {product} available and accurate but cannot promise it will be uninterrupted or error-free. Maintenance and outages of the platforms and providers we rely on can interrupt it.",
        "We improve {product} over time. We do not remove a paid feature you rely on, during a period you paid for, without telling you. You may then cancel and get back the unused part.",
      ],
    },
    {
      id: "liability",
      heading: "13. Liability",
      body: [
        "We are liable without limit for intent and gross negligence, for injury to life, body or health, and under the German Product Liability Act.",
        "For slight negligence we are liable only if we breach an essential obligation, one that makes the contract possible and that you may rely on. Our liability is then limited to the damage that is typical and foreseeable for this kind of contract.",
        "Any further liability is excluded. These limits also protect our staff and agents.",
      ],
    },
    {
      id: "indemnity",
      heading: "14. Claims against us caused by your use",
      body: [
        "If someone claims against us because you used {product} in breach of these terms or the law, you hold us harmless. This covers the claim and the reasonable costs of our defence, unless you are not responsible for the breach.",
        "We tell you about such a claim promptly and do not settle it without talking to you.",
      ],
    },
    {
      id: "changes",
      heading: "15. Changes to these terms",
      body: [
        "We email you any change to these terms at least 30 days before it applies, with the new text. If you object, you may end the contract before the change applies.",
        "If you do not object, the change applies from the date given. Silence never counts as agreement to a higher price or to a change of what you have bought; those need your express consent.",
      ],
    },
    {
      id: "law",
      heading: "16. Law, courts and language",
      body: [
        "German law applies, without the UN Convention on Contracts for the International Sale of Goods. If you are a consumer after all, the mandatory protections of your home country still apply.",
        "If you are a merchant or a public body, the courts of Berlin hear any dispute. We may also sue you where you are based.",
        "These terms exist in English and Spanish. If the two differ, the English text prevails.",
        "If a clause is invalid, the rest stays in force.",
      ],
    },
    {
      id: "contact",
      heading: "17. Contact",
      body: ["Write to us at {contact}, or by post to {company}, {address}."],
    },
  ],
};
